<script setup>
import { formApi } from '@/configs/forms-api';
import { ref, computed } from 'vue';
import { useField, useForm } from 'vee-validate';
import { useI18n } from 'vue-i18n';
import { useRequest } from '@/composables/useRequest';
import { loadRecaptchaScript } from '@/composables/useRecaptcha';
import { useGlobalStore } from '@/stores/global';
import { createSubscriptionSchema } from '@/configs/validation-schemas';

const { t } = useI18n();

const globalStore = useGlobalStore();
const subscriptionSchema = computed(() => createSubscriptionSchema(t));

const loading = ref(false);

const captchaSiteKey = computed(() => globalStore.captchaSiteKey);

const { handleSubmit, resetForm, setErrors } = useForm({
  validationSchema: subscriptionSchema,
  initialValues: {
    email: '',
  },
});

const email = useField('email');

const isRequiredFilled = computed(() =>
  subscriptionSchema.value.isValidSync({ email: email.value.value }),
);

const formResponse = ref({});
const getCaptchaToken = async () => {
  const loaded = await loadRecaptchaScript(captchaSiteKey.value);

  return new Promise((resolve) => {
    if (!loaded || !window.grecaptcha || !captchaSiteKey.value) {
      console.warn('ReCaptcha script not loaded or site key missing');
      resolve(null);
      return;
    }

    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(captchaSiteKey.value, { action: 'subscription' })
        .then((token) => resolve(token))
        .catch((err) => {
          console.error('ReCaptcha execution error:', err);
          resolve(null);
        });
    });
  });
};
const onSubmit = handleSubmit(async (values) => {
  loading.value = true;
  const currentFullUrl = process.client ? window.location.href : '';
  try {
    const token = await getCaptchaToken();
    const res = await useRequest(`/forms/${formApi['subscription']}`, {
      method: 'POST',
      body: {
        email: values.email,
        page: currentFullUrl,
        token: token ? token : {},
      },
    });
    if (res && res.success !== false) {
      if (process.client) {
        window.dataLayer = window.dataLayer || [];
        const translatedFormName = t('subscription.title');
        window.dataLayer.push({
          event: 'form_submit',
          form_name: translatedFormName,
          form_id: 'subscription',
          form_location: currentFullUrl,
          form_status: 'success',
        });
      }
      resetForm();
      formResponse.value = res;
    } else if (res && res.errors) {
      setErrors(res.errors);
      console.warn(res.header || 'Validation Error');
    }
  } catch (e) {
    if (e.data && e.data.errors) {
      setErrors(e.data.errors);
    }
    console.error('Submission error:', e);
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <form class="form-subscription" @submit.prevent="onSubmit">
    <template v-if="formResponse && formResponse?.header">
      <div class="form-subscription__thanks">
        <p class="mb-0 h5">
          {{ formResponse.header || $t('thanks.subscription_success_header') }}
        </p>
        <p v-if="formResponse.content" class="mb-0 body-1-regular thanks__text">
          {{ formResponse.content || $t('thanks.subscription_success_text') }}
        </p>
      </div>
    </template>
    <template v-else>
      <div
        class="form-subscription__item"
        :class="{
          'form-subscription__item--error': !!email.errorMessage.value,
        }"
      >
        <FloatLabel variant="in" class="floatlabel-ontransparent">
          <InputText
            id="aubscriptionEmail"
            v-model="email.value.value"
            class="form-subscription__item-field input-transparent"
            :invalid="!!email.errorMessage.value"
            type="text"
          />
          <label
            for="aubscriptionEmail"
            class="form-subscription__item-label"
            :class="{ 'error-100': !!email.errorMessage.value }"
          >
            {{ $t('subscription.email') }}*
          </label>
        </FloatLabel>
      </div>
      <Button
        :label="$t('subscription.send_button')"
        type="submit"
        class="form-subscription__button button-large-danger link-large"
        :loading="loading"
        :disabled="!isRequiredFilled"
        severity="danger"
        size="large"
      />
      <slot></slot>
    </template>
  </form>
</template>

<style scoped lang="scss">
.form-subscription {
  &__thanks {
    display: flex;
    gap: 24px;
    flex-direction: column;
    text-align: center;
    @include media-breakpoint-up(sm) {
      gap: 32px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 32);
    }
  }
  &__button {
    min-height: 80px;
    padding: 12px 32px;
    border-radius: 0 0 4px 4px;
    @include media-breakpoint-up(sm) {
      min-height: 98px;
    }
    @include media-breakpoint-up(md) {
      padding: 4px 32px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(padding-bottom, 4);
      @include responsive-spacing(padding-left, 32);
      @include responsive-spacing(padding-right, 32);
      @include responsive-spacing(padding-top, 4);
      @include responsive-spacing(border-bottom-left-radius, 4);
      @include responsive-spacing(border-bottom-right-radius, 4);
      @include responsive-spacing(min-height, 98);
    }
  }
}
</style>
